import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Square, Mic, AlertCircle, Lock, Settings } from 'lucide-react';
import { useMicrophonePermission } from '../../shared/hooks/useMicrophonePermission';
import LanguageSelector from './LanguageSelector';

interface EnhancedRecordingBarProps {
  finalText: string;
  interimText: string;
  enhancedText?: string;
  isEnhancing?: boolean;
  error?: string | null;
  audioLevel?: number;
  onStop: () => void;
  showUpgradePrompt?: boolean;
  remainingWords?: number;
  onUpgrade?: () => void;
  micPermission?: ReturnType<typeof useMicrophonePermission>;
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
}

const EnhancedRecordingBar: React.FC<EnhancedRecordingBarProps> = ({ 
  finalText, 
  interimText,
  enhancedText,
  isEnhancing,
  error,
  audioLevel = 0,
  onStop,
  showUpgradePrompt = false,
  remainingWords = 2000,
  onUpgrade,
  micPermission,
  selectedLanguage = 'en',
  onLanguageChange
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const mobileTextRef = useRef<HTMLDivElement>(null);
  const [smoothedAudioLevel, setSmoothedAudioLevel] = useState(0);
  const smoothingRef = useRef<number | null>(null);

  // Auto-scroll to bottom of text every 1 second
  useEffect(() => {
    const scrollInterval = setInterval(() => {
      // Scroll desktop view
      if (textRef.current) {
        textRef.current.scrollTop = textRef.current.scrollHeight;
      }
      // Scroll mobile view
      if (mobileTextRef.current) {
        mobileTextRef.current.scrollTop = mobileTextRef.current.scrollHeight;
      }
    }, 1000);

    return () => clearInterval(scrollInterval);
  }, []); // Empty dependency array - just runs the interval

  // Smooth audio level changes
  useEffect(() => {
    const smoothAudio = () => {
      setSmoothedAudioLevel((prev) => {
        const diff = audioLevel - prev;
        // Slower rise, faster fall
        const smoothingFactor = diff > 0 ? 0.15 : 0.08;
        return prev + diff * smoothingFactor;
      });
      smoothingRef.current = requestAnimationFrame(smoothAudio);
    };

    smoothingRef.current = requestAnimationFrame(smoothAudio);

    return () => {
      if (smoothingRef.current) {
        cancelAnimationFrame(smoothingRef.current);
      }
    };
  }, [audioLevel]);

  const displayText = () => {
    if (isEnhancing) {
      return enhancedText || 'Enhancing text with AI...';
    }
    const text = finalText || interimText;
    if (text) {
      return <span>{text}</span>;
    }
    return <span className="opacity-40 animate-pulse">Listening...</span>;
  };
  
  // Determine what content to show based on state
  const getMainContent = () => {
    // Show upgrade prompt if no credits
    if (showUpgradePrompt) {
      return (
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"
            >
              <span className="text-white text-base sm:text-lg">✨</span>
            </motion.div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                You've reached your monthly limit
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                {remainingWords === 0 ? 'No words remaining' : `${remainingWords} words remaining`}
              </p>
            </div>
          </div>
          {onUpgrade && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onUpgrade}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
            >
              Upgrade to Unlimited
            </motion.button>
          )}
        </div>
      );
    }
    
    // Show permission prompt if needed
    if (micPermission?.needsPermission) {
      return (
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center"
            >
              <Mic className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
            </motion.div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                Microphone access required
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Click "Allow" when prompted to start recording
              </p>
            </div>
          </div>
          {micPermission?.isChecking && (
            <div className="flex items-center space-x-2 text-sky-600">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-4 h-4 border-2 border-sky-600 border-t-transparent rounded-full"
              />
              <span className="text-xs">Requesting...</span>
            </div>
          )}
        </div>
      );
    }
    
    // Show blocked permission message
    if (micPermission?.isBlocked) {
      return (
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-red-100 flex items-center justify-center"
            >
              <Lock className="w-4 sm:w-5 h-4 sm:h-5 text-red-600" />
            </motion.div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                Microphone access blocked
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                {micPermission.error || 'Please enable in browser settings'}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => micPermission.openSettings()}
            className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] sm:text-xs font-medium rounded-md sm:rounded-lg transition-all flex items-center space-x-0.5 sm:space-x-1"
          >
            <Settings className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
            <span className="hidden sm:inline">Open Settings</span>
            <span className="sm:hidden">Settings</span>
          </motion.button>
        </div>
      );
    }
    
    // Show normal streaming text
    return (
      <div className="flex-1 relative">
        <div 
          ref={textRef}
          className="overflow-auto scrollbar-hide h-12"
          style={{
            scrollBehavior: 'smooth',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          <motion.div
            className="text-gray-700 text-xs sm:text-sm leading-5 sm:leading-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {displayText()}
            <motion.span
              className={`inline-block w-0.5 h-4 ml-0.5 align-middle ${
                isEnhancing ? 'bg-blue-400' : 'bg-gray-400'
              }`}
              animate={{
                opacity: [1, 0, 1],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        </div>
        {/* Fade edges for overflow */}
        <div className="absolute left-0 top-0 right-0 h-3 bg-gradient-to-b from-white to-transparent pointer-events-none" />
        <div className="absolute left-0 bottom-0 right-0 h-3 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>
    );
  };

  // Determine bar color based on state
  const getBarColor = () => {
    if (showUpgradePrompt) return 'from-amber-400 via-orange-500 to-amber-400';
    if (micPermission?.isBlocked) return 'from-red-400 via-red-500 to-red-400';
    if (isEnhancing) return 'from-blue-400 via-blue-500 to-blue-400';
    return 'from-red-400 via-red-500 to-red-400';
  };
  
  const getShadowColor = () => {
    if (showUpgradePrompt) return '0 20px 60px -15px rgba(251, 146, 60, 0.3), 0 0 0 1px rgba(251, 146, 60, 0.1)';
    if (micPermission?.isBlocked) return '0 20px 60px -15px rgba(239, 68, 68, 0.3), 0 0 0 1px rgba(239, 68, 68, 0.1)';
    return '0 20px 60px -15px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.03)';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
      }}
      className="fixed bottom-4 sm:bottom-6 md:bottom-8 left-0 right-0 z-50 flex justify-center px-4 sm:px-6 md:px-8"
    >
      <motion.div
        className="bg-white/95 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl"
        style={{
          boxShadow: getShadowColor(),
          overflow: 'visible', // Allow dropdown to show
        }}
        animate={{
          scale: isEnhancing ? 1 : [1, 1 + smoothedAudioLevel * 0.02, 1],
        }}
        transition={{
          duration: isEnhancing ? 0.3 : 0.1,
        }}
      >
        {/* Mobile Layout - Vertical */}
        <div className="sm:hidden">
          {/* Top Section - Recording Status and Text */}
          <div className="p-4">
            {/* Recording Status Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <motion.div
                  className="relative"
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <div className={`absolute inset-0 rounded-full blur-md opacity-50 ${
                    isEnhancing ? 'bg-blue-500' : 'bg-red-500'
                  }`} />
                  <div className={`relative w-3 h-3 rounded-full ${
                    isEnhancing ? 'bg-blue-500' : 'bg-red-500'
                  }`} />
                </motion.div>
                
                <div className="flex items-center space-x-1.5">
                  <Mic className={`w-3.5 h-3.5 ${isEnhancing ? 'text-blue-600' : 'text-red-600'}`} strokeWidth={2} />
                  <span className={`text-xs font-medium ${
                    isEnhancing ? 'text-blue-700' : 'text-red-700'
                  }`}>
                    {isEnhancing ? 'Enhancing' : 'Recording'}
                  </span>
                </div>
              </div>

              {/* Audio Wave Animation for mobile */}
              {!isEnhancing && micPermission?.hasPermission && (
                <motion.div className="flex items-center space-x-1">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-red-400 rounded-full"
                      animate={{
                        height: [
                          `${10 + smoothedAudioLevel * 15}px`,
                          `${16 + smoothedAudioLevel * 20}px`,
                          `${10 + smoothedAudioLevel * 15}px`
                        ],
                      }}
                      transition={{
                        duration: 0.8 - smoothedAudioLevel * 0.3,
                        repeat: Infinity,
                        delay: i * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </motion.div>
              )}
            </div>

            {/* Text Display Area */}
            <div 
              ref={mobileTextRef}
              className="mb-3 relative h-20 overflow-y-auto scrollbar-hide rounded-lg bg-gray-50/50 p-3"
              style={{
                scrollBehavior: 'smooth',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              <motion.div
                className="text-gray-700 text-xs leading-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                {displayText()}
                <motion.span
                  className={`inline-block w-0.5 h-4 ml-0.5 align-middle ${
                    isEnhancing ? 'bg-blue-400' : 'bg-gray-400'
                  }`}
                  animate={{
                    opacity: [1, 0, 1],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </div>

            {/* Bottom Controls - Language and Stop Button */}
            <div className="flex items-center justify-between gap-3">
              {/* Language Selector - Mobile Version */}
              <div className="flex-1">
                <LanguageSelector
                  selectedLanguage={selectedLanguage || 'en'}
                  onLanguageChange={onLanguageChange || (() => {})}
                  disabled={isEnhancing}
                />
              </div>
              
              {/* Stop Button */}
              {!isEnhancing && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onStop}
                  className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 
                           flex items-center justify-center text-white shadow-lg transition-all"
                >
                  <Square className="w-5 h-5" fill="currentColor" strokeWidth={0} />
                </motion.button>
              )}
            </div>

            {/* Error Display for Mobile */}
            {error && !micPermission?.isBlocked && (
              <div className="mt-2 flex items-center space-x-1 text-red-600">
                <AlertCircle className="w-3 h-3" />
                <span className="text-[10px]">{error}</span>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="h-1 bg-gray-100 relative overflow-hidden rounded-b-2xl">
            <motion.div
              className={`absolute left-0 top-0 bottom-0 bg-gradient-to-r ${getBarColor()}`}
              animate={{
                x: ['-100%', '200%'],
              }}
              transition={{
                duration: isEnhancing ? 1.5 : 2,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{ width: '50%' }}
            />
          </div>
        </div>

        {/* Desktop/Tablet Layout - Horizontal (unchanged) */}
        <div className="hidden sm:block">
          <div className="flex items-stretch rounded-2xl sm:rounded-3xl overflow-hidden">
            {/* Language Selector - Left side */}
            <LanguageSelector
              selectedLanguage={selectedLanguage || 'en'}
              onLanguageChange={onLanguageChange || (() => {})}
              disabled={isEnhancing}
            />
            
            {/* Recording/Processing Status Indicator */}
            <div className={`px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-r flex items-center space-x-2 sm:space-x-3 transition-colors ${
              isEnhancing 
                ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200/50' 
                : 'bg-gradient-to-r from-red-50 to-red-100 border-red-200/50'
            }`}>
              <motion.div
                className="relative"
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className={`absolute inset-0 rounded-full blur-md opacity-50 ${
                  isEnhancing ? 'bg-blue-500' : 'bg-red-500'
                }`} />
                <div className={`relative w-3 h-3 rounded-full ${
                  isEnhancing ? 'bg-blue-500' : 'bg-red-500'
                }`} />
              </motion.div>
              
              <div className="flex items-center space-x-1 sm:space-x-2">
                <Mic className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${isEnhancing ? 'text-blue-600' : 'text-red-600'}`} strokeWidth={2} />
                <span className={`text-xs sm:text-sm font-medium ${
                  isEnhancing ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {isEnhancing ? 'Enhancing' : 'Recording'}
                </span>
              </div>

              {/* Audio Wave Animation or Placeholder - maintains consistent width */}
              {!isEnhancing && micPermission?.hasPermission ? (
                <motion.div className="hidden sm:flex items-center space-x-1 w-[34px]">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-red-400 rounded-full"
                      animate={{
                        height: [
                          `${12 + smoothedAudioLevel * 20}px`,
                          `${20 + smoothedAudioLevel * 30}px`,
                          `${12 + smoothedAudioLevel * 20}px`
                        ],
                      }}
                      transition={{
                        duration: 0.8 - smoothedAudioLevel * 0.3,
                        repeat: Infinity,
                        delay: i * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </motion.div>
              ) : (
                // Invisible placeholder to maintain width during enhancing
                <div className="hidden sm:block w-[34px]" />
              )}
            </div>

            {/* Main Content Area */}
            <div className="flex-1 px-3 sm:px-4 md:px-6 py-2 sm:py-3 flex items-center">
              {getMainContent()}
            </div>

            {/* Error Display */}
            {error && !micPermission?.isBlocked && (
              <div className="hidden sm:flex px-2 sm:px-3 md:px-4 py-3 sm:py-4 items-center space-x-1 sm:space-x-2 text-red-600 border-l border-gray-100">
                <AlertCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                <span className="text-[10px] sm:text-xs">{error}</span>
              </div>
            )}

            {/* Stop Button - Always show unless enhancing */}
            {!isEnhancing && (
              <div className="px-3 sm:px-4 py-3 sm:py-4 border-l border-gray-100">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onStop}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 
                           flex items-center justify-center text-white shadow-lg transition-all"
                >
                  <Square className="w-4 sm:w-5 h-4 sm:h-5" fill="currentColor" strokeWidth={0} />
                </motion.button>
              </div>
            )}
          </div>

          {/* Recording/Processing Progress Bar */}
          <div className="h-1 bg-gray-100 relative overflow-hidden">
            <motion.div
              className={`absolute left-0 top-0 bottom-0 bg-gradient-to-r ${getBarColor()}`}
              animate={{
                x: ['-100%', '200%'],
              }}
              transition={{
                duration: isEnhancing ? 1.5 : 2,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{ width: '50%' }}
            />
          </div>
        </div>
      </motion.div>

      {/* Subtle Glow Effect */}
      <motion.div
        className={`absolute inset-0 rounded-3xl blur-3xl -z-10 bg-gradient-to-r ${
          showUpgradePrompt
            ? 'from-amber-400/10 via-orange-400/10 to-amber-400/10'
            : isEnhancing
            ? 'from-blue-400/10 via-indigo-400/10 to-blue-400/10'
            : 'from-red-400/10 via-orange-400/10 to-red-400/10'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Style for hiding scrollbar */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `
      }} />
    </motion.div>
  );
};

export default EnhancedRecordingBar;